"use client";

import type { TimeLimitPopupLimitType } from "@/components/TimeLimitPopup";
import type { UpgradeObjectionReason } from "@/lib/paywall/recovery";
import { ArrowRight, Flame, X } from "lucide-react";
import { useState } from "react";

export type UpgradeRecoverySurface = "session_limit_wrap_up" | "paywall_exit_card";

interface UpgradeRecoveryCardProps {
  surface: UpgradeRecoverySurface;
  currentStreak?: number | null;
  limitType?: TimeLimitPopupLimitType;
  showObjections?: boolean;
  onClose: (action: "continue_free" | "dismiss") => void;
  onUpgrade: (reason?: UpgradeObjectionReason) => void;
  onObjectionSelected?: (reason: UpgradeObjectionReason) => void;
}

const OBJECTION_OPTIONS: Array<{ label: string; reason: UpgradeObjectionReason }> = [
  { label: "It costs too much", reason: "cost" },
  { label: "I need more time", reason: "need_time" },
  { label: "I'm not sure yet", reason: "unsure_value" },
  { label: "Checkout didn't work", reason: "checkout_problem" },
];

export function getRecoveryHeadline(
  surface: UpgradeRecoverySurface,
  currentStreak?: number | null
): string {
  if (typeof currentStreak === "number" && currentStreak >= 2) {
    return surface === "session_limit_wrap_up"
      ? `You're on a ${currentStreak}-day speaking streak.`
      : `You've built a ${currentStreak}-day speaking streak.`;
  }
  if (currentStreak === 1) return "You started a speaking streak today.";
  return surface === "session_limit_wrap_up"
    ? "You showed up and spoke today."
    : "Your speaking progress is saved.";
}

export function getLimitContext(limitType?: TimeLimitPopupLimitType): string | null {
  if (limitType === "daily") return "You've used today's free speaking time.";
  if (limitType === "weekly") return "You've used this week's 3 free conversations.";
  return null;
}

function getObjectionResponse(reason: UpgradeObjectionReason) {
  switch (reason) {
    case "cost":
      return {
        body: "We'll show the best offer currently available for your account.",
        cta: "See my available offer",
        upgrades: true,
      };
    case "need_time":
      return {
        body: "No pressure. Your free practice stays available.",
        cta: "Continue free",
        upgrades: false,
      };
    case "unsure_value":
      return {
        body: "See what unlimited speaking includes before deciding.",
        cta: "See what Pro unlocks",
        upgrades: true,
      };
    case "checkout_problem":
      return {
        body: "Try checkout again. If it still doesn't work, support can help.",
        cta: "Try checkout again",
        upgrades: true,
      };
  }
}

export function UpgradeRecoveryCard({
  surface,
  currentStreak,
  limitType,
  showObjections = false,
  onClose,
  onUpgrade,
  onObjectionSelected,
}: UpgradeRecoveryCardProps) {
  const [selectedObjection, setSelectedObjection] = useState<UpgradeObjectionReason | null>(null);
  const isSessionWrapUp = surface === "session_limit_wrap_up";
  const objectionResponse = selectedObjection ? getObjectionResponse(selectedObjection) : null;

  const handleObjectionSelected = (reason: UpgradeObjectionReason) => {
    setSelectedObjection(reason);
    onObjectionSelected?.(reason);
  };

  return (
    <section
      aria-label={isSessionWrapUp ? "Session wrap-up" : "Speaking progress"}
      className="fixed left-4 right-4 top-20 z-[80] overflow-hidden rounded-xl border border-white/10 bg-[#171717]/[0.98] text-white shadow-2xl shadow-black/40 backdrop-blur sm:left-auto sm:right-5 sm:w-[420px]"
      data-testid="upgrade-recovery-card"
    >
      <div className="h-1 bg-gradient-to-r from-[#FD954F] via-orange-500 to-amber-400" />
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#FD954F]/15 text-[#FD954F]">
            <Flame aria-hidden="true" className="h-5 w-5" />
          </div>
          <div aria-live="polite" className="min-w-0 flex-1">
            <h2 className="pr-8 text-lg font-bold leading-snug">
              {getRecoveryHeadline(surface, currentStreak)}
            </h2>
            {isSessionWrapUp ? (
              <p className="mt-2 text-sm leading-6 text-neutral-300">
                Your progress is saved. Upgrade whenever you want to keep speaking without limits.
              </p>
            ) : (
              <p className="mt-2 text-sm leading-6 text-neutral-300">
                Keep practising free, or unlock unlimited speaking whenever you're ready.
              </p>
            )}
            {isSessionWrapUp && getLimitContext(limitType) ? (
              <p className="mt-3 text-xs font-medium text-neutral-400">
                {getLimitContext(limitType)}
              </p>
            ) : null}
          </div>
          <button
            aria-label="Dismiss speaking progress card"
            className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#FD954F]"
            onClick={() => onClose("dismiss")}
            type="button"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>

        {showObjections && !selectedObjection ? (
          <div className="mt-5 border-t border-white/10 pt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-400">
              What held you back?
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {OBJECTION_OPTIONS.map((option) => (
                <button
                  className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-2 text-xs font-medium text-neutral-200 transition hover:border-[#FD954F]/60 hover:bg-[#FD954F]/10 focus:outline-none focus:ring-2 focus:ring-[#FD954F]"
                  key={option.reason}
                  onClick={() => handleObjectionSelected(option.reason)}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {selectedObjection && objectionResponse ? (
          <div className="mt-5 rounded-lg border border-[#FD954F]/25 bg-[#FD954F]/[0.08] p-4">
            <p className="text-sm leading-6 text-neutral-200">{objectionResponse.body}</p>
            <button
              className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#FD954F] px-4 py-2.5 text-sm font-bold text-[#171717] transition hover:bg-[#ffa96f] focus:outline-none focus:ring-2 focus:ring-white"
              onClick={() =>
                objectionResponse.upgrades ? onUpgrade(selectedObjection) : onClose("continue_free")
              }
              type="button"
            >
              {objectionResponse.cta}
              {objectionResponse.upgrades ? (
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              ) : null}
            </button>
          </div>
        ) : (
          <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
            {isSessionWrapUp ? (
              <>
                <button
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-[#FD954F] px-4 py-2.5 text-sm font-bold text-[#171717] transition hover:bg-[#ffa96f] focus:outline-none focus:ring-2 focus:ring-white"
                  onClick={() => onUpgrade()}
                  type="button"
                >
                  Keep speaking without limits
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </button>
                <button
                  className="min-h-11 rounded-lg px-4 py-2.5 text-sm font-semibold text-neutral-300 transition hover:bg-white/[0.06] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#FD954F]"
                  onClick={() => onClose("continue_free")}
                  type="button"
                >
                  Done for today
                </button>
              </>
            ) : (
              <>
                <button
                  className="min-h-11 rounded-lg border border-white/15 px-4 py-2.5 text-sm font-semibold text-neutral-200 transition hover:border-[#FD954F]/60 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#FD954F]"
                  onClick={() => onUpgrade()}
                  type="button"
                >
                  Unlock unlimited speaking
                </button>
                <button
                  className="min-h-11 flex-1 rounded-lg bg-white px-4 py-2.5 text-sm font-bold text-[#171717] transition hover:bg-neutral-200 focus:outline-none focus:ring-2 focus:ring-[#FD954F]"
                  onClick={() => onClose("continue_free")}
                  type="button"
                >
                  Continue free
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
